import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Card, CardContent, Button, Grid,
  TextField, Paper, Avatar, Chip, LinearProgress, Divider
} from '@mui/material';
import { Chat, Send, ArrowBack } from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { messageAPI } from '../services/api';

const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadConversations(); }, []);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await messageAPI.getConversations();
      setConversations(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openConversation = async (conv) => {
    setSelectedUser(conv);
    try {
      const res = await messageAPI.getConversation(conv.user_id);
      setMessages(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      const res = await messageAPI.send({
        receiver_id: selectedUser.user_id,
        content: newMessage,
      });
      setMessages([...messages, res.data]);
      setNewMessage('');
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Chat color="primary" />
          <Typography variant="h4" fontWeight={700}>Messages</Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Grid container spacing={2} sx={{ height: '70vh' }}>
          {/* Conversations List */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ height: '100%', borderRadius: 3, overflow: 'auto' }} elevation={0}>
              {conversations.length === 0 && !loading && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Chat sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No conversations yet</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Start a conversation from an item page
                  </Typography>
                </Box>
              )}
              {conversations.map((conv) => (
                <Box
                  key={conv.user_id}
                  onClick={() => openConversation(conv)}
                  sx={{
                    p: 2, cursor: 'pointer',
                    bgcolor: selectedUser?.user_id === conv.user_id ? '#eff6ff' : 'white',
                    borderBottom: '1px solid #f1f5f9',
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                      {conv.user_name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" fontWeight={600}>{conv.user_name}</Typography>
                        {conv.unread_count > 0 && (
                          <Chip label={conv.unread_count} size="small" color="primary" sx={{ height: 20 }} />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {conv.last_message}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>

          {/* Chat Area */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ height: '100%', borderRadius: 3, display: 'flex', flexDirection: 'column' }} elevation={0}>
              {selectedUser ? (
                <>
                  {/* Chat Header */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button startIcon={<ArrowBack />} onClick={() => setSelectedUser(null)}
                      sx={{ display: { md: 'none' } }} size="small" />
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                      {selectedUser.user_name?.charAt(0)}
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight={600}>{selectedUser.user_name}</Typography>
                  </Box>

                  {/* Messages */}
                  <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {messages.map((msg) => {
                      const isMe = msg.sender_id === user?.id;
                      return (
                        <Box key={msg.id} sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                          <Box sx={{
                            maxWidth: '70%', p: 1.5, borderRadius: 2,
                            bgcolor: isMe ? 'primary.main' : '#f1f5f9',
                            color: isMe ? 'white' : 'text.primary',
                          }}>
                            <Typography variant="body2">{msg.content}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}>
                              {new Date(msg.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Message Input */}
                  <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth size="small" placeholder="Type a message..."
                      value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      multiline maxRows={3}
                    />
                    <Button variant="contained" onClick={handleSend} sx={{ minWidth: 48 }}>
                      <Send />
                    </Button>
                  </Box>
                </>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chat sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">Select a conversation to start chatting</Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default MessagesPage;


